import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { ToastService } from '../services/toast';
import { StorageService } from '../services/storage';

/**
 * Pull the most meaningful human message out of ANY error-response shape so the app never
 * shows a bare "Bad request". Handles, in priority order:
 *   1. our API envelope           { message }
 *   2. ASP.NET model validation   { errors: { Field: ["is required", …] }, title }  ← the usual 400
 *   3. ProblemDetails             { detail } / { title }
 *   4. a plain-text body          "Something failed"   (ignores HTML error pages)
 * Returns null when there's nothing usable, so callers fall back to their generic message.
 */
function extractApiMessage(error: any): string | null {
  const body = error?.error;
  if (body == null) return null;

  if (typeof body === 'string') {
    const s = body.trim();
    return s && !s.startsWith('<') ? s : null; // skip HTML error pages
  }
  if (typeof body === 'object') {
    if (typeof body.message === 'string' && body.message.trim()) return body.message.trim();

    // Model-validation 400: the real messages live under `errors`; `title` is the generic
    // "One or more validation errors occurred." — join the per-field messages instead.
    if (body.errors && typeof body.errors === 'object') {
      const msgs = ([] as string[])
        .concat(...Object.values(body.errors).map((v) => (Array.isArray(v) ? v : [v])))
        .filter((m): m is string => typeof m === 'string' && m.trim().length > 0);
      if (msgs.length) return msgs.join(' ');
    }
    if (typeof body.detail === 'string' && body.detail.trim()) return body.detail.trim();
    if (
      typeof body.title === 'string' &&
      body.title.trim() &&
      body.title !== 'One or more validation errors occurred.'
    )
      return body.title.trim();
  }
  return null;
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const toast = inject(ToastService);
  const router = inject(Router);
  const storage = inject(StorageService);

  return next(req).pipe(
    catchError((error) => {
      // A 401 on a request made WITHOUT a session token is a failed login attempt
      // (wrong password / user not found), NOT an expired session. Let the login
      // component surface the server's real message — don't toast or redirect here.
      const hasSession = !!storage.getLocal('token');

      // Receipt + Certificate + report-file downloads are surfaced by their own
      // components/services (ReceiptDownloader, CoiDownloader, the report-scheduler page), which read
      // the blob/text error body for the real envelope message. Skip the generic toast here so the
      // user sees ONE accurate message, not two. (401 still falls through so an expired session is handled.)
      if (
        (req.url.includes('/PaymentReceipt/') ||
          req.url.includes('/Coi/') ||
          req.url.includes('/HospicashLetter/') ||
          req.url.includes('/ReportSchedule/DownloadFile')) &&
        error.status !== 401
      ) {
        return throwError(() => error);
      }

      // The backend's real message (envelope / validation / ProblemDetails), when present.
      const apiMessage = extractApiMessage(error);

      switch (error.status) {
        case 0:
          toast.error('Unable to reach the server. Check your connection.');
          break;
        case 401:
          if (hasSession) {
            // authInterceptor sits UPSTREAM of this one, so this catchError runs first. When a
            // refresh token exists, stay quiet and rethrow — authInterceptor will transparently
            // refresh and retry. Terminal cleanup happens here only when the session cannot be
            // refreshed (no refresh token) or the refresh call itself came back 401.
            const canRefresh = !!storage.getLocal('refreshToken');
            const isRefreshCall = req.url.includes('/Login/RefreshToken');
            if (!canRefresh || isRefreshCall) {
              toast.error(apiMessage || 'Session expired. Please login again');
              storage.clearAll();
              router.navigate(['/login']);
            }
          }
          // else: pre-auth failure → leave it to the component's error handler.
          break;
        case 403:
          toast.error(apiMessage || 'Access denied');
          break;
        case 500:
          // Never surface a raw server exception/stack; only a clean envelope message.
          toast.error(apiMessage || 'Server error. Please try again later');
          break;
        default:
          // 400 / 404 / 409 / 422 / everything else: show the backend's real, user-friendly
          // message (e.g. "PAN is required", "Member already paid") instead of a bare status.
          toast.error(apiMessage || 'Something went wrong. Please try again.');
      }

      return throwError(() => error);
    })
  );
};
